<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import AppHeader from '@/components/AppHeader.vue';
import DataForm from '@/components/DataForm.vue';
import PieChartView from '@/components/PieChart.vue';
import TrendChart from '@/components/TrendChart.vue';
import HistoryTable from '@/components/HistoryTable.vue';
import StatsPanel from '@/components/StatsPanel.vue';
import ImportDialog from '@/components/ImportDialog.vue';
import EditDialog from '@/components/EditDialog.vue';
import Toast from '@/components/Toast.vue';
import { useSectorData, useClearAll } from '@/composables/useSectorData';
import { useSectorStats } from '@/composables/useSectorStats';
import { DailyRecord } from '@/types/sector';

const { data, isLoading, isError, error, refetch } = useSectorData();
const clearAll = useClearAll();

const dailyData = computed(() => data.value?.dailyData ?? []);
const sectorScores = computed(() => data.value?.sectorScores ?? {});

const stats = useSectorStats(dailyData, sectorScores);

const showImport = ref(false);
const editingRecord = ref<DailyRecord | null>(null);
const toastMsg = ref<{ text: string; kind: 'success' | 'error' | 'info' } | null>(null);

function showToast(text: string, kind: 'success' | 'error' | 'info' = 'info') {
  toastMsg.value = { text, kind };
}
function dismissToast() {
  toastMsg.value = null;
}

function onSaved(msg: string) {
  showToast(msg, 'success');
}
function onError(msg: string) {
  showToast(msg, 'error');
}

async function handleClear() {
  if (!confirm('确定要清空所有数据吗？此操作不可恢复。')) return;
  try {
    await clearAll.mutateAsync();
    showToast('已清空所有数据', 'success');
  } catch (e) {
    showToast((e as Error).message ?? '清空失败', 'error');
  }
}

onMounted(() => {
  // 主题检测
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    document.documentElement.classList.add('dark');
  }
});
</script>

<template>
  <div class="min-h-screen">
    <AppHeader />

    <main class="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">
      <Toast
        v-if="toastMsg"
        :message="toastMsg.text"
        :kind="toastMsg.kind"
        @close="dismissToast"
      />

      <div v-if="isLoading" class="card animate-fade-in">
        <div class="flex items-center gap-3">
          <div class="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          <span class="text-ink-muted">正在加载数据...</span>
        </div>
      </div>

      <div v-else-if="isError" class="card border border-danger/30 bg-danger/5">
        <p class="text-danger">加载失败：{{ (error as Error)?.message ?? '未知错误' }}</p>
        <button class="btn btn-primary mt-3" @click="refetch()">重试</button>
      </div>

      <template v-else>
        <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <DataForm
            :daily-data="dailyData"
            @saved="onSaved"
            @error="onError"
            @import="showImport = true"
            @clear="handleClear"
          />
          <PieChartView :stats="stats" />
        </div>

        <div class="mt-5">
          <TrendChart :stats="stats" />
        </div>

        <div class="mt-5">
          <HistoryTable
            :records="dailyData"
            @edit="(r) => (editingRecord = r)"
          />
        </div>

        <div class="mt-5">
          <StatsPanel :stats="stats" />
        </div>
      </template>
    </main>

    <ImportDialog
      v-if="showImport"
      @close="showImport = false"
      @done="(msg) => { showImport = false; onSaved(msg); }"
      @error="onError"
    />

    <EditDialog
      v-if="editingRecord"
      :record="editingRecord"
      @close="editingRecord = null"
      @saved="(msg) => { editingRecord = null; onSaved(msg); }"
      @error="onError"
    />
  </div>
</template>
